import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import type { WaterPlacement } from "./SceneEnvironment.helpers";
import { createBoulderGeometry, layoutBoulders } from "./shoreRocks";

const ignoreRaycast = () => undefined;

/**
 * Granite with moss on the upward faces, speckled lichen, and a dark wet
 * band just above the waterline (also a damp foot where a boulder meets the
 * forest floor). World-space, so every instance weathers differently.
 */
function applyBoulderWeathering(
	material: THREE.MeshStandardMaterial,
	waterY: number,
): THREE.MeshStandardMaterial {
	material.onBeforeCompile = (shader) => {
		shader.uniforms.uRockWaterY = { value: waterY };
		shader.vertexShader = shader.vertexShader
			.replace(
				"#include <common>",
				`#include <common>
varying vec3 vRockWorld;
varying float vRockUp;`,
			)
			.replace(
				"#include <begin_vertex>",
				`#include <begin_vertex>
mat4 rockModel = modelMatrix;
#ifdef USE_INSTANCING
rockModel = modelMatrix * instanceMatrix;
#endif
vRockWorld = (rockModel * vec4(transformed, 1.0)).xyz;
vRockUp = normalize(mat3(rockModel) * objectNormal).y;`,
			);
		shader.fragmentShader = shader.fragmentShader
			.replace(
				"#include <common>",
				`#include <common>
uniform float uRockWaterY;
varying vec3 vRockWorld;
varying float vRockUp;
float rockHash(vec3 p) {
	p = fract(p * 0.3183099 + 0.1);
	p *= 17.0;
	return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}
float rockNoise(vec3 x) {
	vec3 i = floor(x);
	vec3 f = fract(x);
	f = f * f * (3.0 - 2.0 * f);
	return mix(
		mix(mix(rockHash(i), rockHash(i + vec3(1, 0, 0)), f.x),
			mix(rockHash(i + vec3(0, 1, 0)), rockHash(i + vec3(1, 1, 0)), f.x), f.y),
		mix(mix(rockHash(i + vec3(0, 0, 1)), rockHash(i + vec3(1, 0, 1)), f.x),
			mix(rockHash(i + vec3(0, 1, 1)), rockHash(i + vec3(1, 1, 1)), f.x), f.y),
		f.z);
}`,
			)
			.replace(
				"#include <color_fragment>",
				`#include <color_fragment>
float rockN1 = rockNoise(vRockWorld * 2.7);
float rockN2 = rockNoise(vRockWorld * 9.0 + 3.1);
float rockN3 = rockNoise(vRockWorld * 31.0);
vec3 rockColour = mix(vec3(0.21, 0.205, 0.195), vec3(0.36, 0.35, 0.33), rockN1)
	* (0.8 + 0.35 * rockN2) * (0.9 + 0.2 * rockN3);
float rockLichen = smoothstep(0.72, 0.8, rockNoise(vRockWorld * 6.0 + 11.0)) * (1.0 - smoothstep(0.5, 0.9, vRockUp));
rockColour = mix(rockColour, vec3(0.55, 0.56, 0.47), rockLichen * 0.6);
float rockMoss = smoothstep(0.5, 0.85, vRockUp + (rockN1 - 0.5) * 0.9 + (rockN3 - 0.5) * 0.2)
	* smoothstep(0.25, 0.5, rockNoise(vRockWorld * 1.3 + 7.0));
vec3 mossColour = mix(vec3(0.13, 0.19, 0.05), vec3(0.3, 0.36, 0.1), rockN2) * (0.85 + 0.3 * rockN3);
rockColour = mix(rockColour, mossColour, rockMoss);
float rockWet = 1.0 - smoothstep(uRockWaterY + 0.02, uRockWaterY + 0.12 + 0.05 * rockN2, vRockWorld.y);
rockColour *= 1.0 - rockWet * 0.5;
diffuseColor.rgb = rockColour;`,
			)
			.replace(
				"#include <roughnessmap_fragment>",
				`#include <roughnessmap_fragment>
roughnessFactor = mix(roughnessFactor, 0.32, rockWet * (1.0 - rockMoss));`,
			);
	};
	material.customProgramCacheKey = () => `boulder-weathering-v1|${waterY}`;
	return material;
}

export function SceneShoreRocks({
	placement,
	groundY,
	site,
}: {
	placement: WaterPlacement;
	groundY: number;
	site: SiteConditions;
}) {
	const geometry = useMemo(() => createBoulderGeometry(), []);
	const boulders = useMemo(
		() => layoutBoulders(placement, groundY, site),
		[placement, groundY, site],
	);
	const waterY = placement.center[1];
	const material = useMemo(
		() =>
			applyBoulderWeathering(
				new THREE.MeshStandardMaterial({
					color: 0xffffff,
					roughness: 0.93,
					metalness: 0,
					envMapIntensity: 0.35,
				}),
				waterY,
			),
		[waterY],
	);
	useEffect(() => () => geometry.dispose(), [geometry]);
	useEffect(() => () => material.dispose(), [material]);

	return (
		<instancedMesh
			name="dressing-boulders"
			args={[geometry, material, boulders.length]}
			receiveShadow
			raycast={ignoreRaycast}
			ref={(mesh) => {
				if (!mesh || typeof mesh.setMatrixAt !== "function") return;
				boulders.forEach((boulder, i) => {
					mesh.setMatrixAt(i, boulder.matrix);
				});
				mesh.instanceMatrix.needsUpdate = true;
				mesh.computeBoundingSphere();
			}}
		/>
	);
}
